import * as React from 'react'

import { renderers } from '@markdoc/markdoc'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'

import ContentImage from '@/components/content/ContentImage'
import Cta from '@/components/content/Cta'
import Iframe from '@/components/content/Iframe'
import Skittle from '@/components/content/Skittle'
import Stat from '@/components/content/Stat'
import Stats from '@/components/content/Stats'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import Vimeo from '@/components/Vimeo'
import YouTube from '@/components/YouTube'
import { cacheControl } from '@/http.server'
import { findLp } from '@/models/lp.server'
import { pageHelper } from '@/request'

type LoaderData = typeof loader

export async function loader({ params, url }: LoaderFunctionArgs) {
  const lp = findLp(params['*'] as string)

  if (!lp) {
    throw new Response('Not Found', { status: 404 })
  }

  const { root } = pageHelper(url)

  return {
    lp: lp,
    root: root
  }
}

export const headers: HeadersFunction = () => ({
  'Cache-Control': cacheControl
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const {
    lp: { meta },
    root
  } = loaderData

  const title = `${meta.title} — PlanetScale`
  const image = `${root}${meta.image}`

  return [
    { title },
    { name: 'description', content: meta.description },
    { name: 'robots', content: meta.robots },
    { property: 'og:url', content: meta.canonical },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: meta.description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: meta.canonical },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: meta.description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: meta.canonical }
  ]
}

export default function LandingPage() {
  const { lp } = useLoaderData<LoaderData>()

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl px-3 sm:px-5 lg:px-12'>
        <article>
          {renderers.react(lp.body, React, {
            components: {
              ContentImage,
              Cta,
              Iframe,
              Skittle,
              Stat,
              Stats,
              Vimeo,
              YouTube
            }
          })}
        </article>
      </main>

      <SiteFooter />
    </>
  )
}
